import { DangerCard } from '@/components/DangerCard/DangerCard'
import { DeleteClusterModal } from '@/components/Modals/DeleteClusterModal'
import { useRBACPermissions } from '@/contexts/useRBACPermissions'
import { useDisclosure } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

export type DeleteClusterDangerZoneProps = Readonly<{
  /**
   * Organization ID for the cluster
   */
  orgId: string
  /**
   * Cluster ID to delete
   */
  clusterId: string
  /**
   * Display name for the cluster (optional, defaults to clusterId)
   */
  clusterName?: string
  /**
   * Whether the cluster is currently being deleted
   */
  isDeleting?: boolean
  /**
   * Callback function to execute when delete is confirmed
   */
  onDelete: (orgId: string, clusterId: string) => void
  /**
   * Optional tooltip description shown when button is disabled
   */
  tooltipDescription?: string
}>

/**
 * DeleteClusterDangerZone component
 *
 * Provides a consistent danger zone UI for deleting clusters with:
 * - Role-based access control (disabled for non-admin users)
 * - Confirmation modal
 * - Custom disable conditions support
 *
 * @example
 * ```tsx
 * <DeleteClusterDangerZone
 *   orgId={orgId}
 *   clusterId={clusterId}
 *   clusterName="my-cluster"
 *   isDeleting={clusterState === 'DELETING'}
 *   onDelete={(orgId, clusterId) => deleteCluster(orgId, clusterId)}
 * />
 * ```
 */
export function DeleteClusterDangerZone({
  orgId,
  clusterId,
  clusterName,
  isDeleting = false,
  onDelete,
  tooltipDescription,
}: DeleteClusterDangerZoneProps) {
  const { t } = useTranslation()
  const { open, onOpen, onClose } = useDisclosure()
  const { hasAdminRights } = useRBACPermissions()
  const isAdmin = hasAdminRights()

  function handleDelete() {
    onDelete(orgId, clusterId)
    onClose()
  }

  const isButtonDisabled = !isAdmin || isDeleting

  const permissionWarning = isAdmin
    ? undefined
    : String(t('deleteCluster.dangerZone.adminTooltip'))
  const tooltipMessage = tooltipDescription || permissionWarning

  return (
    <>
      <DangerCard
        title={String(t('deleteCluster.dangerZone.title'))}
        description={String(t('deleteCluster.dangerZone.description'))}
        onAction={onOpen}
        dangerousActionText={String(t('deleteCluster.dangerZone.buttonText'))}
        isDisabled={isButtonDisabled}
        tooltipDescription={tooltipMessage}
      />

      <DeleteClusterModal
        clusterName={clusterName ?? clusterId}
        isOpen={open}
        onClose={onClose}
        handleDelete={handleDelete}
      />
    </>
  )
}
